import {Property} from './property/property.model';
import {PropertyType} from './property/property-type.enum';
import {DescriptorEnum} from '../config/descriptors/descriptor.enum';
import {DescriptorLink} from './property/descriptor-link.model';
import {SuggestionComplex} from '../search/suggestions/suggestion-complex';
import {Filter} from '../search/search-list/filters/filter-block/filter-item/filter';
import * as moment from 'moment';
import {ObjectLink} from '../search/object-detail/object-title/object-link.model';
import {ObjectDetailTitle} from '../search/search-list/results/result-item/object-detail-title.model';
import {el} from "@angular/platform-browser/testing/src/browser_util";


export class AppUtils {

  public static generateRandom(min=1000, max=10000) {
    return Math.floor(min + Math.random()*(max + 1 - min))
  }

  public static getCurrentPage(): string {
    let currentUrl = window.location.href.split('?')[0];
    let n = currentUrl.lastIndexOf('/');
    return currentUrl.substring(n);
  }

  public static getCurrentHostName() {
    return window.location.href;
  }

  public static createHighlightByPattern(toBeHighlighted: string, pattern: string) {
    if (pattern) {
      let split = pattern.replace(new RegExp('[.\/]', 'g'), '').split(' ');
      split.forEach(val => {
        let regExp = new RegExp('(^|)(' + val + ')(|$)', 'ig');
        let match = toBeHighlighted.match(regExp);
        if (match) {
          if (match[0] != '') {
            toBeHighlighted = toBeHighlighted.replace(new RegExp('(^|)(' + val + ')(|$)', 'ig'), `<b>${match[0]}</b>`);
          }
        }
      });
    }
    return toBeHighlighted;
  }

  public static mapObjectToProperties(object: any, withDescriptorLink = true): Property[] {
    let properties = [];
    if (!object) {
      return properties;
    }
    Object.keys(object).forEach((key => {
      let value: number | string | boolean | Property[], type: PropertyType;
      let element = object[key] && object[key].length === 1 ?  object[key][0]: object[key];
      let nestedArray = [];
      type = this.resolvePropertyType(element, withDescriptorLink);
      value = this.resolveValue(type, value, element, nestedArray, withDescriptorLink);

      if (nestedArray.length === 0) {
        properties.push(new Property({type: type, name: key, value: value}))
      } else {
        properties.push(new Property({name: key, type: type, value: nestedArray}));
      }
    }));
    return properties;
  }

  public static getSuggestionComplexes(items: SuggestionComplex[], type: String): SuggestionComplex[] {
    if (!items) {
      return [];
    } else {
      return items
        .filter(item => item.type == type)
        .filter(item => item.hasResults());
    }
  }

  public static flattenArray(array: Array<any>): Array<any> {
    const flattenedArray: Array<any> = [].concat(...array);
    return flattenedArray.some(Array.isArray)
      ? this.flattenArray(flattenedArray)
      : flattenedArray;
  }

  public static phraseToFilter(phrase: string): Filter {
    const selected = phrase && phrase.trim() && phrase.trim().length > 0;
    return new Filter({ field: '_q', value: phrase, selected});
  }

  public static formatDate(date: number){
    if (date) {
      return moment(date).format('DD.MM.YYYY');
    }
    return '-';
  }

  private static resolveValue(type: PropertyType, value: any, element, nestedArray, withDescriptorLink: boolean) {
    if (type === PropertyType.PROPERTY && element !== null) {
      value = this.mapObjectToProperties(element, withDescriptorLink);
    } else if (type === PropertyType.DESCRIPTOR) {
      value = [new DescriptorLink({
        id: element['nlpId'] || element['id'],
        type: <DescriptorEnum>DescriptorEnum[element['type'].toUpperCase().split('_')[0]],
        value: element['preferredName'] || element['value']
      })]
    }
    else if (type === PropertyType.ARRAY) {

      element.forEach(value => {
        if (typeof(value) === 'string') {
          nestedArray.push(value);
        } else if (value === null) {
          nestedArray.push('');
        } else {
          if (this.isDescriptor(value) && withDescriptorLink) {
            nestedArray.push(new DescriptorLink({
              id: value['nlpId'] || value['id'],
              type: <DescriptorEnum>DescriptorEnum[value['type'].toUpperCase().split('_')[0]],
              value: value['preferredName'] || value['value']
            }))
          } else {
            nestedArray.push(this.mapObjectToProperties(value, withDescriptorLink));
          }
        }
      })
    } else {
      value = element !== null ? element : '';
    }
    return value;
  }

  private static resolvePropertyType(value, withDescriptorLink: boolean): PropertyType {
    if (value instanceof Array) {
      return PropertyType.ARRAY;
    }

    if (this.isDescriptor(value) && withDescriptorLink) {
      return PropertyType.DESCRIPTOR;
    }

    let propertyType = typeof value;
    switch (propertyType) {
      case 'string': {
        return PropertyType.STRING;
      }
      case 'number': {
        return PropertyType.NUMBER;
      }
      case 'boolean': {
        return PropertyType.BOOLEAN;
      }
      default : {
        return PropertyType.PROPERTY
      }
    }
  }

  private static isDescriptor(value) {
    return value && (value.hasOwnProperty('nlpId') || value.hasOwnProperty('id')) && value.hasOwnProperty('type') && this.isDescriptorType(value['type']);
  }

  private static isDescriptorType(type: string) {
    let descriptorEnumElement = DescriptorEnum[type.toUpperCase().split('_')[0]];
    return descriptorEnumElement !== undefined;
  }

  static isMuseumRecord(record: any) {
    return record['type'] == 'museum_object';
  }

  static isWorkRecord(record: any) {
    return record['type'] == 'work';
  }

  static hasId(record: any){
    return !!record.id;
  }

  private static getLink(descriptorLink: DescriptorLink): string {
    return `/${descriptorLink.type}/${descriptorLink.id}`;
  }

  public static createTitleWrapper(record: any, linkTitle: boolean = true): ObjectDetailTitle{
    return new ObjectDetailTitle({
      authors: AppUtils.createAuthors(record),
      title: AppUtils.createTitle(record, linkTitle),
      extras: AppUtils.createExtras(record)
    });
  }

  public static createAuthors(record: any): ObjectLink[] {
    if (AppUtils.isWorkRecord(record)) {
      let authorValue = record['work_presentation_main_creator'];
      if (authorValue) {
        if (Array.isArray(authorValue)) {
          return authorValue.map(AppUtils.createAuthor);
        } else if (AppUtils.isDescriptor(authorValue)) {
          return [new ObjectLink({link: AppUtils.getLink(authorValue), label: authorValue.value})];
        }
      }
    } else {
      let authorValue = record['mus_object_creator'];
      if (!authorValue) {
        authorValue = record['creator'];
      }
      if (authorValue) {
        if (Array.isArray(authorValue)) {
          authorValue = authorValue.join(', ');
        }
        return [new ObjectLink({label: authorValue})];
      }
    }
    return [];
  }

  public static createAuthor(author: any): ObjectLink{
    if (author && !!author.value){
      if (Array.isArray(author.value)){
        return AppUtils.createAuthor(author.value[0]);
      }else if (AppUtils.isDescriptor(author)){
        return new ObjectLink({link: AppUtils.getLink(author), label: author.value});
      }
    }
    return new ObjectLink({});
  }

  public static createTitle(record: any, linkTitle: boolean): ObjectLink {
    const link = linkTitle && AppUtils.hasId(record) ? `/${record['type']}/${record['id']}` : null;
    if (AppUtils.isWorkRecord(record)) {
      return new ObjectLink({link, label: `${record['work_title_pref']}`});
    } else if (AppUtils.isMuseumRecord(record)) {
      return new ObjectLink({link, label: `${record['mus_object_title']}`});
    } else if (record['title]']) {
      return new ObjectLink({link, label: record['title']});
    } else if (record['public_dataset_title']) {
      return new ObjectLink({link: `/public_dataset/${record['id']}`, label: record['public_dataset_title']});
    }
  }

  public static createExtras(record: any): string[] {
    if (AppUtils.isWorkRecord(record)) {
      if (record['eForm']) {
        return [record['eForm']];
      }
    }
    return [];
  }

  public static checkCollectionIdInHref(): boolean {
    return window.location.href.toLowerCase().includes('collectionid');
  }
  public static checkSearchWorkInHref(): boolean {
    return window.location.href.toLowerCase().includes('/detail/work/') || window.location.href.toLowerCase().includes('/materialization/');
  }

}
